/**
 * What a document is made of, so a person can look instead of guess.
 *
 * Every real finding in the memory work came from opening a document and
 * seeing its shape: that 88.8% of a project registry's ops were one per
 * character of text, that a session carrying 220,674 ops held eight scalar
 * fields and no content, that a 99 MB document was 99 MB of somebody's prose
 * and so was not bloated at all. Each of those took a throwaway script. This
 * is that script, kept.
 *
 * It answers three questions, which are the three that mattered:
 *
 *   - What is in here? Fields, by how much state they hold.
 *   - What did it cost? Ops, by action and by field.
 *   - Why did it cost that? Which fields are text, one op per character, and
 *     which are stored as a scalar.
 */

import * as A from '@automerge/automerge/slim'

export interface FieldReport {
  /** The path, e.g. `commentIndex.*.documentId`. `*` stands for every key of a map. */
  path: string
  /** How many values were seen at this path. */
  count: number
  /** Characters held here, which is ops if it is stored as text. */
  chars: number
  /** How it is stored. `text` costs one op per character. */
  storedAs: 'text' | 'scalar' | 'number' | 'boolean' | 'other'
}

export interface DocumentReport {
  ops: number
  /** False when the state was too large to measure and had to be counted. */
  stateExact: boolean
  changes: number
  actors: number
  /** Bytes of the state as JSON, which is what the document actually says. */
  stateBytes: number
  /** Ops by Automerge action: `set`, `makeText`, `del` and so on. */
  opsByAction: Record<string, number>
  /** The fields holding the most characters, heaviest first. */
  fields: FieldReport[]
  /** Characters held in text fields: the ops a conversion could remove. */
  charsInText: number
}

/**
 * A key that is an id rather than a field name.
 *
 * Ids in this system are long and have no separators; a field name is short
 * and lower-case. Getting this wrong only changes a label, never a number.
 */
function looksLikeId(k: string): boolean {
  return k.length >= 16 || /[A-Z0-9]{6,}/.test(k)
}

function storageOf(v: unknown): FieldReport['storedAs'] {
  if (v instanceof A.ImmutableString) return 'scalar'
  if (typeof v === 'string') return 'text'
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  return 'other'
}

/**
 * Walk the state, collapsing map keys to `*`.
 *
 * A registry with 1,350 comments has 1,350 identical shapes under different
 * ids. Listing them separately buries the one fact worth having, which is that
 * `commentIndex.*.documentId` holds 38,000 characters across all of them.
 */
function walk(
  value: unknown,
  path: string,
  into: Map<string, FieldReport>,
  depth: number,
): void {
  if (depth > 6 || value == null) return
  const storedAs = storageOf(value)
  if (storedAs !== 'other') {
    const chars =
      storedAs === 'text' || storedAs === 'scalar' ? String(value).length : 0
    const row = into.get(path) ?? { path, count: 0, chars: 0, storedAs }
    row.count++
    row.chars += chars
    // A path holding both is reported as text, because that is what costs.
    if (storedAs === 'text') row.storedAs = 'text'
    into.set(path, row)
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) walk(item, `${path}[]`, into, depth + 1)
    return
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>)
    // A map keyed by id collapses to `*`; a record with named fields does not.
    //
    // NEVER THE TOP LEVEL, whatever its size. A project registry has twenty-odd
    // named fields at the root, and collapsing them turned every row into
    // `*.*.title`, which says a title is heavy without saying whose. The name
    // of the collection is most of the answer.
    const keyed = depth > 0 && keys.length > 12 && keys.every(looksLikeId)
    for (const k of keys) {
      walk(
        (value as Record<string, unknown>)[k],
        `${path}${path ? '.' : ''}${keyed ? '*' : k}`,
        into,
        depth + 1,
      )
    }
  }
}

/** Everything worth knowing about one document. */
export function analyseDocument(doc: unknown): DocumentReport {
  const opsByAction: Record<string, number> = {}
  const actors = new Set<string>()
  let ops = 0
  let changes = 0
  for (const change of A.getAllChanges(doc as never)) {
    changes++
    const decoded = A.decodeChange(change)
    actors.add(decoded.actor)
    for (const op of decoded.ops) {
      ops++
      opsByAction[op.action] = (opsByAction[op.action] ?? 0) + 1
    }
  }

  const fields = new Map<string, FieldReport>()
  walk(doc, '', fields, 0)
  const rows = [...fields.values()].sort((a, b) => b.chars - a.chars)

  // MEASURED, OR COUNTED, NEVER ZERO. `JSON.stringify` throws on a document
  // whose state is larger than a JS string can hold, and a session with nine
  // thousand entries is. Reporting 0.00 MB for an 85 million character
  // document is worse than reporting nothing: it reads as "this is empty"
  // beside an op count in the hundreds of thousands, which is how it was first
  // read. The walk has already counted every character, so fall back to that.
  let stateBytes = 0
  let stateExact = true
  try {
    stateBytes = JSON.stringify(doc)?.length ?? 0
  } catch {
    stateExact = false
    stateBytes = rows.reduce((t, r) => t + r.chars, 0)
  }

  return {
    ops,
    changes,
    actors: actors.size,
    stateExact,
    stateBytes,
    opsByAction,
    fields: rows,
    charsInText: rows
      .filter((r) => r.storedAs === 'text')
      .reduce((n, r) => n + r.chars, 0),
  }
}

/**
 * The document itself, cut down to something a person can look at.
 *
 * Looking at the data is how most of this was understood, but a session holds
 * eighty-five million characters and no pane survives being handed that. So
 * every map and list is cut to `maxEntries` and every string to `maxChars`,
 * and what was cut says so in place, e.g. `{ "...": "9,078 more entries" }`.
 *
 * A WRAPPER READS AS ITS CHARACTERS. An `ImmutableString` would otherwise
 * serialise as `{}` and a reader would think the field was empty, which is the
 * same trap that made a converted field read as blank in `reads.ts`.
 */
export function boundedState(
  value: unknown,
  opts: { maxEntries?: number; maxChars?: number; depth?: number } = {},
): unknown {
  const maxEntries = opts.maxEntries ?? 20
  const maxChars = opts.maxChars ?? 400
  const depth = opts.depth ?? 0
  if (value instanceof A.ImmutableString) {
    return boundedState(String(value), { ...opts, depth })
  }
  if (typeof value === 'string') {
    return value.length > maxChars
      ? `${value.slice(0, maxChars)}... (${value.length.toLocaleString()} characters)`
      : value
  }
  if (value == null || typeof value !== 'object') return value
  if (depth > 6) return '...'
  if (Array.isArray(value)) {
    const head = value
      .slice(0, maxEntries)
      .map((v) => boundedState(v, { ...opts, depth: depth + 1 }))
    if (value.length > maxEntries) {
      head.push(`... ${(value.length - maxEntries).toLocaleString()} more`)
    }
    return head
  }
  const keys = Object.keys(value as Record<string, unknown>)
  const out: Record<string, unknown> = {}
  for (const k of keys.slice(0, maxEntries)) {
    out[k] = boundedState((value as Record<string, unknown>)[k], {
      ...opts,
      depth: depth + 1,
    })
  }
  if (keys.length > maxEntries) {
    out['...'] = `${(keys.length - maxEntries).toLocaleString()} more entries`
  }
  return out
}
